import React, { useEffect, useState } from "react";
import client from "../lib/api";
import { useApp } from "../context/AppContext";
import { Card, Button, Badge, Modal } from "../components/ui";
import { LANGS, APP_FULL } from "../i18n";
import { Copy, Check, Download, Megaphone, FileArchive, Sparkles, Rocket, History, FileText } from "lucide-react";
import { appLogo, appIcon, APP_ICONS } from "../appLogos";

const APP_KEYS = ["family_tree", "holdings", "currency", "benefits", "master_enabler"];
const HERO_KEYS = ["family_tree", "holdings", "currency", "benefits"];
const heroSrc = (k, lang) => `/store-shots/heroes/${k}_${lang}.png`;
const FIELDS = [
  { k: "title", label: "Title", limit: 30 },
  { k: "short_description", label: "Short description", limit: 80 },
  { k: "full_description", label: "Full description", limit: 800 },
  { k: "keywords", label: "Keywords", limit: 100 },
];

export default function StoreListings() {
  const { lang } = useApp();
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({ version: "", notes: {} });
  const [versions, setVersions] = useState([]);
  const [liveVersion, setLiveVersion] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [active, setActive] = useState(lang || "ENG");
  const [copied, setCopied] = useState("");
  const [histOpen, setHistOpen] = useState(false);
  const [hist, setHist] = useState({ versions: [], promotions: [], live: null });
  const [diffA, setDiffA] = useState("");
  const [diffB, setDiffB] = useState("");
  const [diffNotes, setDiffNotes] = useState({ A: {}, B: {} });

  useEffect(() => { client.get("/store-listings").then((r) => setData(r.data)).catch(() => setData({})); }, []);
  useEffect(() => { loadVersions(); }, []);

  const loadVersions = async () => {
    const [v, live] = await Promise.all([
      client.get("/release-notes/versions").then((r) => r.data).catch(() => []),
      client.get("/release-notes/live").then((r) => r.data.version).catch(() => null),
    ]);
    setVersions(v); setLiveVersion(live);
    loadNotes(live || v[0]?.version);
  };
  const loadNotes = async (version) => {
    const url = version ? `/release-notes?version=${encodeURIComponent(version)}` : "/release-notes";
    const n = await client.get(url).then((r) => r.data).catch(() => ({ version: "", notes: {} }));
    setNotes(n || { version: "", notes: {} });
  };

  const generateVersion = async () => {
    const version = window.prompt("New version number (e.g. 1.1.0):", "");
    if (!version) return;
    const highlights = window.prompt("Key highlights for this release (optional):", "") || undefined;
    setGenBusy(true);
    try {
      const { data: nd } = await client.post("/release-notes/generate", { version, highlights });
      setNotes(nd);
      await client.get("/release-notes/versions").then((r) => setVersions(r.data));
    } catch (e) { window.alert("Generation failed, please retry."); }
    finally { setGenBusy(false); }
  };

  const promoteLive = async () => {
    if (!notes.version) return;
    if (!window.confirm(`Promote v${notes.version} to the live/published release notes? This updates the downloadable store kit.`)) return;
    try {
      const { data } = await client.post("/release-notes/promote", { version: notes.version });
      setLiveVersion(data.live_version);
      window.alert(`v${data.live_version} is now the live release notes.`);
    } catch (e) { window.alert("Promote failed, please retry."); }
  };

  const openHistory = async () => {
    setHistOpen(true);
    const h = await client.get("/release-notes/history").then((r) => r.data).catch(() => ({ versions: [], promotions: [], live: null }));
    setHist(h);
    const b = h.versions[0]?.version || "";
    const a = h.versions[1]?.version || h.versions[0]?.version || "";
    setDiffA(a); setDiffB(b);
    loadDiff(a, b);
  };
  const loadDiff = async (a, b) => {
    const fetchNotes = (v) => v ? client.get(`/release-notes?version=${encodeURIComponent(v)}`).then((r) => r.data.notes || {}).catch(() => ({})) : Promise.resolve({});
    const [A, B] = await Promise.all([fetchNotes(a), fetchNotes(b)]);
    setDiffNotes({ A, B });
  };

  const exportChangelogPdf = async () => {
    try {
      const res = await client.get(`/release-notes/changelog.pdf?lang=${active}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `changelog_${active}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { window.alert("PDF export failed, please retry."); }
  };

  const copy = async (id, text) => {
    try { await navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(""), 1500); } catch {}
  };

  const copyAllApp = (ak) => {
    const c = (data?.[active] || {})[ak] || {};
    const wn = notes?.notes?.[active]?.[ak] || "";
    const block = `${APP_FULL[ak]}\n\nTitle: ${c.title || ""}\nShort description: ${c.short_description || ""}\n\nFull description:\n${c.full_description || ""}\n\nKeywords: ${c.keywords || ""}${wn ? `\n\nWhat's New (v${notes.version || "1.0.0"}):\n${wn}` : ""}`;
    copy(`all-${active}-${ak}`, block);
  };

  const downloadZip = async () => {
    try {
      const res = await client.get("/store-listings/zip", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "store_listings_all_languages.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const downloadHeroesZip = async () => {
    try {
      const res = await client.get("/store-listings/heroes.zip", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "store_heroes_all_languages.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const downloadMd = () => {
    const langData = data?.[active] || {};
    const langName = LANGS.find((l) => l.code === active)?.native || active;
    let md = `# App Store Listings — ${langName} (${active})\n_Brand: Full Option Family Office_\n`;
    APP_KEYS.forEach((ak) => {
      const c = langData[ak]; if (!c) return;
      const wn = notes?.notes?.[active]?.[ak] || "";
      md += `\n## ${APP_FULL[ak]}\n\n**Title:** ${c.title || ""}\n\n**Short description:** ${c.short_description || ""}\n\n**Full description:**\n\n${c.full_description || ""}\n\n**Keywords:** ${c.keywords || ""}\n${wn ? `\n**What's New (v${notes.version || "1.0.0"}):**\n\n${wn}\n` : ""}`;
    });
    const blob = new Blob([md], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `store_listings_${active}.md`; a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!data) return <div className="opacity-60">Loading store listings…</div>;
  const hasData = Object.keys(data).length > 0;
  const langData = data[active] || {};

  return (
    <div className="space-y-6 fadeup">
      <div className="flex flex-wrap items-center gap-3">
        <Megaphone className="text-[var(--primary)]" />
        <h1 className="font-head text-2xl sm:text-3xl">Store Listings</h1>
        <span className="opacity-60 text-sm">Localized titles, descriptions & keywords for Google Play & the App Store</span>
        <div className="ms-auto flex flex-wrap items-center gap-2">
          <span className="text-xs opacity-60">Release notes:</span>
          <select value={notes.version || ""} onChange={(e) => loadNotes(e.target.value)} data-testid="version-select"
            className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1.5 text-sm">
            {versions.length === 0 && <option value="">—</option>}
            {versions.map((v) => <option key={v.version} value={v.version}>{`v${v.version}${v.version === liveVersion ? " • LIVE" : ""}`}</option>)}
          </select>
          {notes.version && (notes.version === liveVersion
            ? <Badge color="success" data-testid="live-badge">LIVE</Badge>
            : <Button variant="ghost" onClick={promoteLive} data-testid="promote-live"><Rocket size={16} /> Promote to live</Button>)}
          <Button variant="ghost" onClick={generateVersion} disabled={genBusy} data-testid="generate-version">
            <Sparkles size={16} /> {genBusy ? "Generating…" : "New version"}
          </Button>
          <Button variant="ghost" onClick={openHistory} data-testid="open-history"><History size={16} /> History</Button>
          <Button variant="ghost" onClick={downloadZip} data-testid="download-listings-zip"><FileArchive size={16} /> Download all 15 (ZIP)</Button>
          <Button variant="ghost" onClick={downloadHeroesZip} data-testid="download-heroes-zip"><FileArchive size={16} /> Download all heroes (48 ZIP)</Button>
          <Button onClick={downloadMd} data-testid="download-listings-md"><Download size={16} /> Download .md</Button>
        </div>
      </div>

      {!hasData && <Card className="opacity-70">No listings found. Run the generator to create localized copy.</Card>}

      {hasData && (
        <>
          <div className="flex flex-wrap gap-2" data-testid="lang-tabs">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setActive(l.code)} data-testid={`lang-tab-${l.code}`}
                className={`rounded-full px-3 py-1 text-sm lift ${active === l.code ? "bg-[var(--primary)] text-white" : "bg-black/5 dark:bg-white/10"}`}>
                {l.native}
              </button>
            ))}
          </div>

          <div className="grid gap-4">
            {APP_KEYS.map((ak) => {
              const c = langData[ak];
              if (!c) return null;
              return (
                <Card key={ak} className="space-y-3" data-testid={`listing-${ak}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <img src={appLogo(ak)} alt="" data-testid={`listing-thumb-${ak}`} className="h-12 w-12 shrink-0 rounded-lg bg-black/5 dark:bg-white/5 object-contain p-1" />
                      <div>
                        <div className="font-head text-lg">{APP_FULL[ak]}</div>
                        {APP_ICONS[ak] && (
                          <a href={appIcon(ak)} download={`${ak}_app_icon_1024.png`} data-testid={`listing-icon-dl-${ak}`}
                            className="flex items-center gap-1 text-xs text-[var(--primary)] hover:underline">
                            <Download size={12} /> Download app icon (1024×1024)
                          </a>
                        )}
                      </div>
                    </div>
                    <button onClick={() => copyAllApp(ak)} data-testid={`copyall-${ak}`}
                      className="flex shrink-0 items-center gap-1 rounded-md border border-black/15 dark:border-white/15 px-2.5 py-1 text-xs lift">
                      {copied === `all-${active}-${ak}` ? <><Check size={13} /> Copied all</> : <><Copy size={13} /> Copy all</>}
                    </button>
                  </div>
                  {HERO_KEYS.includes(ak) && (
                    <div className="rounded-md border border-black/10 dark:border-white/10 p-3" data-testid={`hero-block-${ak}`}>
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-xs font-medium opacity-70">Store hero graphic (1600×900) · {active}</span>
                        <a href={heroSrc(ak, active)} download={`${ak}_store_hero_${active}.png`} data-testid={`hero-dl-${ak}`}
                          className="ms-auto flex items-center gap-1 text-xs text-[var(--primary)] hover:underline">
                          <Download size={13} /> Download hero
                        </a>
                      </div>
                      <a href={heroSrc(ak, active)} target="_blank" rel="noopener noreferrer">
                        <img src={heroSrc(ak, active)} alt={`${APP_FULL[ak]} store hero`} data-testid={`hero-img-${ak}`}
                          className="w-full rounded-md border border-black/10 dark:border-white/10 object-contain" />
                      </a>
                    </div>
                  )}
                  {FIELDS.map((f) => {
                    const val = c[f.k] || "";
                    const id = `${active}-${ak}-${f.k}`;
                    const over = val.length > f.limit;
                    return (
                      <div key={f.k} className="rounded-md border border-black/10 dark:border-white/10 p-3">
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-xs font-medium opacity-70">{f.label}</span>
                          <Badge color={over ? "danger" : "neutral"}>{val.length}/{f.limit}</Badge>
                          <button onClick={() => copy(id, val)} data-testid={`copy-${id}`}
                            className="ms-auto flex items-center gap-1 text-xs text-[var(--primary)] hover:underline">
                            {copied === id ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                          </button>
                        </div>
                        <div className={`text-sm ${f.k === "full_description" ? "whitespace-pre-wrap" : ""}`}>{val}</div>
                      </div>
                    );
                  })}
                  {(() => {
                    const note = notes?.notes?.[active]?.[ak] || "";
                    if (!note) return null;
                    const id = `wn-${active}-${ak}`;
                    const over = note.length > 500;
                    return (
                      <div className="rounded-md border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-3" data-testid={`whatsnew-${ak}`}>
                        <div className="mb-1 flex items-center gap-2">
                          <span className="text-xs font-medium opacity-70">What's New (v{notes.version || "1.0.0"})</span>
                          <Badge color={over ? "danger" : "info"}>{note.length}/500</Badge>
                          <button onClick={() => copy(id, note)} data-testid={`copy-${id}`}
                            className="ms-auto flex items-center gap-1 text-xs text-[var(--primary)] hover:underline">
                            {copied === id ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
                          </button>
                        </div>
                        <div className="whitespace-pre-wrap text-sm">{note}</div>
                      </div>
                    );
                  })()}
                </Card>
              );
            })}
          </div>
        </>
      )}

      <Modal open={histOpen} onClose={() => setHistOpen(false)} title="Version history & changelog" testid="history-modal">
        <div className="space-y-5">
          <div className="flex items-center gap-2">
            <Button onClick={exportChangelogPdf} data-testid="export-changelog-pdf"><FileText size={16} /> Export changelog PDF ({active})</Button>
            <span className="text-xs opacity-60">Branded, with Alberta arbitration footer · supports all 15 languages</span>
          </div>
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-60">Versions</div>
            <div className="space-y-2">
              {hist.versions.length === 0 && <div className="text-sm opacity-60">No versions yet.</div>}
              {hist.versions.map((v) => (
                <div key={v.version} className="flex flex-wrap items-center gap-2 rounded-md border border-black/10 dark:border-white/10 p-2 text-sm" data-testid={`hist-version-${v.version}`}>
                  <span className="font-head font-medium">v{v.version}</span>
                  {v.is_live && <Badge color="success">LIVE</Badge>}
                  <span className="opacity-60 text-xs">{(v.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  {v.created_by && <span className="opacity-60 text-xs">· {v.created_by}</span>}
                  {v.highlights && <span className="w-full text-xs opacity-70">“{v.highlights}”</span>}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-60">Promotion log</div>
            <div className="space-y-1 text-sm">
              {hist.promotions.length === 0 && <div className="text-sm opacity-60">No promotions yet.</div>}
              {hist.promotions.map((p, i) => (
                <div key={i} className="flex items-center gap-2" data-testid="promotion-log-row">
                  <Rocket size={13} className="text-[var(--primary)]" />
                  <span className="font-medium">v{p.version}</span>
                  <span className="opacity-60 text-xs">promoted {(p.promoted_at || "").slice(0, 16).replace("T", " ")}{p.promoted_by ? ` by ${p.promoted_by}` : ""}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide opacity-60">
              Compare ({LANGS.find((l) => l.code === active)?.native})
            </div>
            <div className="mb-2 flex items-center gap-2 text-sm">
              <select value={diffA} onChange={(e) => { setDiffA(e.target.value); loadDiff(e.target.value, diffB); }} data-testid="diff-a"
                className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1">
                {hist.versions.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
              </select>
              <span className="opacity-50">vs</span>
              <select value={diffB} onChange={(e) => { setDiffB(e.target.value); loadDiff(diffA, e.target.value); }} data-testid="diff-b"
                className="rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 px-2 py-1">
                {hist.versions.map((v) => <option key={v.version} value={v.version}>v{v.version}</option>)}
              </select>
            </div>
            <div className="space-y-3">
              {APP_KEYS.map((ak) => {
                const a = (diffNotes.A?.[active] || {})[ak] || "";
                const b = (diffNotes.B?.[active] || {})[ak] || "";
                const changed = a !== b;
                return (
                  <div key={ak} className={`rounded-md border p-2 ${changed ? "border-[#F1C21B]" : "border-black/10 dark:border-white/10"}`}>
                    <div className="mb-1 flex items-center gap-2 text-xs font-medium opacity-70">{ak.replace("_", " ")} {changed && <Badge color="warning">changed</Badge>}</div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="whitespace-pre-wrap rounded bg-black/5 dark:bg-white/10 p-2 text-xs">{a || "—"}</div>
                      <div className="whitespace-pre-wrap rounded bg-black/5 dark:bg-white/10 p-2 text-xs">{b || "—"}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
